import { useState } from 'react';
import type { Address, Hash } from 'viem';
import { useWalletClient } from 'wagmi';

import type { TransactionQueueItem } from 'components';
import MAX_UINT256 from 'constants/maxUint256';
import { getContractAddress, positionRouterAbi } from 'libs/contracts';
import { stakingV2Abi } from 'libs/contracts/abis/bittensorPrecompiles';
import { wAlphaAbi, wsTAOAbi } from 'libs/contracts/abis/bittensorWrappers';
import { STAKING_V2_ADDRESS } from 'libs/contracts/config/bittensorPrecompiles';
import { wrapPositionRouterError } from 'libs/errors';
import { useAccountAddress, useChainId, usePublicClient } from 'libs/wallet';
import type { VToken } from 'types';
import { buffer } from 'utilities';

import type { BittensorNativeSupplySource } from '../../SupplyForm/getBittensorSupplySourceTokens';
import {
  failedStepStateReducer,
  getRouterQueueStep,
  getStakingAllowance,
  runRouterQueueStep,
} from '../../shared/routerQueueCore';
import { invalidateRouterQueries } from '../../shared/routerQueueInvalidation';
import {
  RAO_WEI,
  calculateSlippageFloor,
  computeRouterDeadline,
  computeWrappedOutFloor,
  floorToRao,
} from '../../shared/routerSlippage';

export type RouterRepaySource =
  | { kind: 'native-tao' }
  | { kind: 'native-alpha'; source: BittensorNativeSupplySource };

export interface RouterRepayQueuePlanStep extends TransactionQueueItem {
  shouldSkip: boolean;
}

interface ExecuteRouterRepayQueueInput {
  amountMantissa: bigint;
  paymentSource: RouterRepaySource;
  slippagePercentage: number;
  vToken: VToken;
  repayFullLoan?: boolean;
}

export const useRouterRepayQueue = () => {
  const { accountAddress } = useAccountAddress();
  const { chainId } = useChainId();
  const { publicClient } = usePublicClient();
  const { data: walletClient } = useWalletClient({ account: accountAddress });
  const [items, setItems] = useState<TransactionQueueItem[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const updateItem = (id: string, status: TransactionQueueItem['status']) => {
    setItems((currentItems) =>
      currentItems.map((item) => (item.id === id ? { ...item, status } : item)),
    );
  };

  const runStep = ({
    action,
    ...step
  }: RouterRepayQueuePlanStep & { action: () => Promise<Hash> }) =>
    runRouterQueueStep({ step, action, updateItem, publicClient });

  const execute = async ({
    amountMantissa,
    paymentSource,
    slippagePercentage,
    vToken,
    repayFullLoan = false,
  }: ExecuteRouterRepayQueueInput) => {
    if (!accountAddress || !walletClient) {
      throw new Error('Wallet is not connected');
    }

    const routerAddress = getContractAddress({ name: 'PositionRouter', chainId });
    const poolComptrollerAddress = getContractAddress({
      name: 'LegacyPoolComptroller',
      chainId,
    });

    if (!routerAddress || !poolComptrollerAddress) {
      throw new Error('PositionRouter or LegacyPoolComptroller address not configured');
    }

    setItems([]);
    setIsRunning(true);

    try {
      const symbol = vToken.underlyingToken.symbol;

      // Full-repay buffer covers accrued interest between sign and mine —
      // without it, dust debt is left behind. Router refunds over-payment.
      const targetedAmount = repayFullLoan ? buffer({ amountMantissa }) : amountMantissa;

      let stakingAllowance = 0n;
      if (paymentSource.kind === 'native-alpha') {
        stakingAllowance = await getStakingAllowance({
          accountAddress,
          publicClient,
          routerAddress: routerAddress as Address,
          netuid: paymentSource.source.netuid,
        });
      }

      const setupSteps: RouterRepayQueuePlanStep[] = [];
      if (paymentSource.kind === 'native-alpha') {
        setupSteps.push({
          id: 'approve-native-alpha',
          label: 'Approve native alpha spend',
          shouldSkip: stakingAllowance >= targetedAmount,
          status: 'idle',
        });
      }

      const plan: RouterRepayQueuePlanStep[] = [
        ...setupSteps,
        {
          id: 'router-repay',
          label: `Repay ${symbol}`,
          shouldSkip: false,
          status: 'idle',
        },
      ];
      setItems(plan);

      if (paymentSource.kind === 'native-alpha') {
        const nativeAlphaApprovalStep = getRouterQueueStep(plan, 'approve-native-alpha');
        await runStep({
          ...nativeAlphaApprovalStep,
          action: () =>
            walletClient.writeContract({
              address: STAKING_V2_ADDRESS as Address,
              abi: stakingV2Abi,
              functionName: 'approve',
              args: [
                routerAddress as Address,
                paymentSource.source.netuid,
                BigInt(MAX_UINT256.toFixed()),
              ],
              account: accountAddress,
            }),
        });
      }

      let minSharesOut: bigint;
      let effectiveAmount = targetedAmount;
      if (paymentSource.kind === 'native-tao') {
        effectiveAmount = floorToRao(targetedAmount);
        // RAO floor can swallow a sub-RAO buffer. Compare floored-buffered vs.
        // floored-PRE-buffer (not floored-buffered) and pad to +1 RAO so the
        // chain sees a strictly-greater-than-debt amount in that edge case.
        if (repayFullLoan && effectiveAmount <= floorToRao(amountMantissa)) {
          effectiveAmount = floorToRao(amountMantissa) + RAO_WEI;
        }
        if (effectiveAmount === 0n) {
          throw new Error('TAO repay amount must be at least 1 RAO (1e-9 TAO)');
        }
        const wsTaoRate = await publicClient.readContract({
          address: vToken.underlyingToken.address,
          abi: wsTAOAbi,
          functionName: 'exchangeRate',
        });
        minSharesOut = computeWrappedOutFloor({
          nativeAmount: effectiveAmount,
          exchangeRate: wsTaoRate,
          slippagePercentage,
        });
      } else if (paymentSource.kind === 'native-alpha') {
        // Live wAlpha rate read (block-by-block accrual on chain 945).
        const wAlphaRate = await publicClient.readContract({
          address: vToken.underlyingToken.address,
          abi: wAlphaAbi,
          functionName: 'exchangeRate',
        });
        minSharesOut = computeWrappedOutFloor({
          nativeAmount: effectiveAmount,
          exchangeRate: wAlphaRate,
          slippagePercentage,
        });
      }

      const deadline = computeRouterDeadline();
      const finalStep = getRouterQueueStep(plan, 'router-repay');

      await runStep({
        ...finalStep,
        action: () => {
          if (paymentSource.kind === 'native-tao') {
            return walletClient.writeContract({
              address: routerAddress as Address,
              abi: positionRouterAbi,
              functionName: 'repayTAO',
              args: [vToken.address, minSharesOut, deadline],
              value: effectiveAmount,
              account: accountAddress,
            });
          }
          if (paymentSource.kind === 'native-alpha') {
            return walletClient.writeContract({
              address: routerAddress as Address,
              abi: positionRouterAbi,
              functionName: 'repay',
              args: [vToken.address, effectiveAmount, minSharesOut, deadline],
              account: accountAddress,
            });
          }
          throw new Error(`Unsupported router repay source: ${paymentSource satisfies never}`);
        },
      });

      invalidateRouterQueries({
        accountAddress,
        chainId,
        poolComptrollerAddress: poolComptrollerAddress as Address,
        routerAddress: routerAddress as Address,
        vTokenAddresses: [vToken.address],
        tokenAddress: vToken.underlyingToken.address,
        netuid: paymentSource.kind === 'native-alpha' ? paymentSource.source.netuid : undefined,
      });
    } catch (error) {
      setItems(failedStepStateReducer);
      throw wrapPositionRouterError(error);
    } finally {
      setIsRunning(false);
    }
  };

  return { execute, isRunning, items };
};
